import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface PendingItemUpdate {
  item_id: string;
  order_id: string;
  pieces?: number;
  pages?: number;
  weight: number;
  price: number;
  addimages_ids?: number[];
}

interface CartUpdatesContextType {
  pendingUpdates: Map<string, PendingItemUpdate>;
  addPendingUpdate: (itemId: string, update: PendingItemUpdate) => void;
  removePendingUpdate: (itemId: string) => void;
  clearAllPendingUpdates: () => void;
  hasPendingUpdates: boolean;
  getPendingUpdate: (itemId: string) => PendingItemUpdate | undefined;
}

const CartUpdatesContext = createContext<CartUpdatesContextType | undefined>(undefined);

export const CartUpdatesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pendingUpdates, setPendingUpdates] = useState<Map<string, PendingItemUpdate>>(new Map());

  const addPendingUpdate = (itemId: string, update: PendingItemUpdate) => {
    setPendingUpdates(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, update);
      return newMap;
    });
  };

  const removePendingUpdate = (itemId: string) => {
    setPendingUpdates(prev => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });
  };

  const clearAllPendingUpdates = () => {
    setPendingUpdates(new Map());
  };

  const getPendingUpdate = (itemId: string) => {
    return pendingUpdates.get(itemId);
  };

  const hasPendingUpdates = pendingUpdates.size > 0;

  return (
    <CartUpdatesContext.Provider
      value={{
        pendingUpdates,
        addPendingUpdate,
        removePendingUpdate,
        clearAllPendingUpdates,
        hasPendingUpdates,
        getPendingUpdate,
      }}
    >
      {children}
    </CartUpdatesContext.Provider>
  );
};

export const useCartUpdates = () => {
  const context = useContext(CartUpdatesContext);
  if (!context) {
    throw new Error('useCartUpdates must be used within a CartUpdatesProvider');
  }
  return context;
};
